import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Container, Spinner, Button } from 'react-bootstrap';
import { getCommunityByIdWebApi, getCommunityDetailByIdWebApi } from '../../api/communities';
import DummyAvatar from '../../assets/images/avatar.png';
import './CommunityDetail.scss';

export const CommunityDetailGuest = () => {
  const location = useLocation();
  const id = location.state?.id;
  const navigate = useNavigate();

  const [communityData, setCommunityData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [descExpanded, setDescExpanded] = useState(false);

  const community = communityData || {
    name: 'Loading Community...',
    description: 'Fetching details...',
    coverImage: '',
    type: 'free',
    subscriptionFee: 0,
    language: 'English',
    category: { name: 'General' },
    createdAt: new Date(),
  };

  useEffect(() => {
    fetchCommunity();
  }, [id]);

  const fetchCommunity = async () => {
    try {
      setLoading(true);
      setError('');
      let res;
      try {
        res = await getCommunityByIdWebApi(id);
      } catch (e) {
        res = await getCommunityDetailByIdWebApi(id);
      }

      const communityObj = res?.data?.community || res?.data || res?.community || (res?._id ? res : null);

      if (communityObj) {
        setCommunityData({
          ...communityObj,
          membersCount: res?.members?.approved || res?.membersCount || communityObj?.membersCount || 0,
        });
      } else {
        setError('Community not found');
      }
    } catch (err) {
      setError('Failed to load community details. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleLoginRedirect = () => {
    navigate('/login?redirect=' + encodeURIComponent(window.location.pathname));
  };

  const handleShare = async () => {
    const shareUrl = community?.shareUrlWeb || community?.shareUrl || window.location.href;
    const text = `Check out ${community?.name || 'this community'} on Yoospeak!`;

    if (navigator.share) {
      try {
        await navigator.share({ title: community?.name || 'Community', text, url: shareUrl });
      } catch (err) {
        console.log('Share failed:', err);
      }
    } else {
      try {
        await navigator.clipboard.writeText(shareUrl);
        alert('Share link copied to clipboard!');
      } catch (err) {
        console.error('Failed to copy link:', err);
      }
    }
  };

  if (error || (!loading && !communityData)) {
    return (
      <Container className="py-5 text-center">
        <div style={{ padding: '60px 20px' }}>
          <i className="bi bi-exclamation-circle d-block mb-3" style={{ fontSize: '3rem', color: '#6B7280' }}></i>
          <h5 className="fw-bold mb-2">{error || 'Community not found'}</h5>
          <Button variant="primary" className="rounded-pill px-4 py-2 mt-2" onClick={() => navigate('/communities')} style={{ backgroundColor: '#6C3BFF', border: 'none' }}>
            Back to Communities
          </Button>
        </div>
      </Container>
    );
  }

  const categoryName = (community.category && typeof community.category === 'object')
    ? community.category.name
    : (community.categoryName || community.category || '');
  const membersCount = community.membersCount || 0;

  const description = community.description || '';
  const shortDesc = description.length > 220 ? description.slice(0, 220) + '...' : description;
  const rawTopics = community?.topics || community?.tags || [];
  const topics = rawTopics.slice(0, 5).map(t =>
    typeof t === 'string' ? { name: t, count: 0 } : { name: t.name || t.title || 'Topic', count: t.count || 0 }
  );

  return (
    <div className="community-detail-page py-4 position-relative">
      {loading && (
        <div className="position-fixed top-0 start-0 w-100 h-100 d-flex flex-column justify-content-center align-items-center" style={{
          zIndex: 9999,
          background: 'rgba(255, 255, 255, 0.4)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          transition: 'all 0.3s ease-in-out'
        }}>
          <div className="p-4 rounded-4 shadow bg-white bg-opacity-75 text-center d-flex flex-column align-items-center" style={{ border: '1px solid rgba(255, 255, 255, 0.5)' }}>
            <Spinner animation="border" style={{ color: '#6C3BFF', width: '3rem', height: '3rem' }} />
            <span className="mt-3 fw-bold text-navy" style={{ letterSpacing: '0.5px' }}>Loading community...</span>
          </div>
        </div>
      )}
      <Container fluid="xl">
        <section className="profile-banner-section bg-white border rounded-4 overflow-hidden">
          {/* ── Banner ── */}
          <div className="cd-banner">
            <img
              src={community.coverImage || 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1200&h=400&fit=crop'}
              alt={community.name}
              className="cd-banner__img"
              onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1200&h=400&fit=crop'; }}
            />
            <div className="cd-banner__actions">
              <button className="cd-btn-share" onClick={handleShare}>
                <i className="bi bi-share"></i> Share
              </button>
            </div>
          </div>

          {/* ── Community Info Card ── */}
          <div className="cd-info-card bg-white p-4">
            <div className="cd-info-top">
              {/* Logo */}
              <div className="cd-logo-wrap">
                <img
                  src={community.coverImage || community.logo || DummyAvatar}
                  alt={community.name}
                  className="cd-logo border border-3 border-white rounded-circle object-fit-cover shadow"
                  onError={(e) => { e.target.src = DummyAvatar; }}
                />
              </div>

              {/* Name + subtitle + tags */}
              <div className="cd-info-body">
                <div className="cd-name text-dark mb-1">{community.name}</div>
                <p className="cd-subtitle">{community.owner?.full_name ? `${community.owner.full_name} ${community.name}` : community.name}</p>
                <div className="cd-tags">
                  {community.language && (
                    <span className="cd-tag cd-tag--lang">
                      <i className="bi bi-translate"></i> {community.language}
                    </span>
                  )}
                  <span className="cd-tag cd-tag--students">
                    <i className="bi bi-people-fill"></i> {membersCount} Students
                  </span>
                  {categoryName && (
                    <span className="cd-tag cd-tag--cat">
                      <i className="bi bi-chat-dots-fill"></i> {categoryName}
                    </span>
                  )}
                </div>
              </div>

              {/* Login button */}
              <div className="cd-info-actions">
                <Button className="cd-btn-joined border-0 px-4 py-2" onClick={handleLoginRedirect} style={{ backgroundColor: '#6C3BFF', fontWeight: '600' }}>
                  Login to Join
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* ── Main Content Area ── */}
        <div className="cd-main-content mt-4">
          {/* About Section directly displayed */}
          <div className="cd-about bg-white border rounded-4 p-4 shadow-sm h-100">
            <div className="d-flex justify-content-between align-items-center border-bottom pb-3 mb-4">
              <h5 className="fw-bold mb-0 text-navy d-flex align-items-center">
                <i className="bi bi-info-circle me-2 text-primary fs-4"></i>
                About this Community
              </h5>
            </div>

            <div className="about-content">
              <div className="desc-box bg-light rounded-4 p-4 mb-4">
                <h6 className="fw-bold text-secondary mb-3 text-uppercase" style={{ letterSpacing: '1px', fontSize: '0.85rem' }}>Description</h6>
                <div className="text-dark fs-6" style={{ lineHeight: '1.7' }}
                  dangerouslySetInnerHTML={{ __html: descExpanded ? description : shortDesc }}
                />
                {description.length > 220 && (
                  <button
                    onClick={() => setDescExpanded(!descExpanded)}
                    className="btn btn-link text-primary p-0 mt-2 fw-semibold text-decoration-none"
                  >
                    {descExpanded ? 'Show Less' : 'Read More...'}
                  </button>
                )}
              </div>

              {topics && topics.length > 0 && (
                <div className="topics-box bg-light rounded-4 p-4 mb-4">
                  <h6 className="fw-bold text-secondary mb-3 text-uppercase" style={{ letterSpacing: '1px', fontSize: '0.85rem' }}>Topics & Skills</h6>
                  <div className="d-flex flex-wrap gap-2">
                    {topics.map((t, idx) => (
                      <span key={idx} className="badge bg-white text-dark border px-3 py-2 rounded-pill shadow-sm">
                        {t.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="text-center mt-5 p-4 bg-light rounded-4 border">
                <h5 className="fw-bold text-dark mb-3">Want to see more?</h5>
                <p className="text-secondary mb-4">Log in to view community events, members, lessons, and join the conversation!</p>
                <Button variant="primary" className="rounded-pill px-5 py-2 shadow-sm" onClick={handleLoginRedirect} style={{ backgroundColor: '#6C3BFF', border: 'none' }}>
                  Login / Sign Up
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
};
